import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft, BookmarkSimple, Calendar, MapPin, Globe, FileText, ArrowUpRight, CalendarPlus, CheckCircle, Star, Books, ShareNetwork } from "@phosphor-icons/react";
import { format, differenceInDays, isPast } from "date-fns";
import { toast } from "sonner";
import { api, API } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { shareUrl as nativeShare, hapticLight, hapticSuccess } from "@/lib/native";
import { confImage } from "@/lib/images";
import TravelSidebar from "@/components/TravelSidebar";

export default function ConferenceDetail() {
  const { id } = useParams();
  const { t } = useTranslation();
  const { user, refresh } = useAuth();
  const [conf, setConf] = useState(null);
  const [loading, setLoading] = useState(true);
  const [attendingCount, setAttendingCount] = useState(0);
  const [reviews, setReviews] = useState({ items: [], count: 0, avg: 0 });
  const [newRating, setNewRating] = useState(5);
  const [newReview, setNewReview] = useState("");

  useEffect(() => {
    setLoading(true);
    api.get(`/conferences/${id}`).then((r) => setConf(r.data)).finally(() => setLoading(false));
    api.get(`/conferences/${id}/attending-count`).then((r) => setAttendingCount(r.data.count || 0));
    loadReviews();
    // eslint-disable-next-line
  }, [id]);

  function loadReviews() {
    api.get(`/conferences/${id}/reviews`).then((r) => setReviews(r.data));
  }

  const isBookmarked = user?.bookmarks?.includes(id);
  const isGoing = user?.attending?.includes(id);

  async function toggleBookmark() {
    if (!user || !user.role) { toast.error(t("common.pleaseSignIn")); return; }
    try {
      if (isBookmarked) { await api.delete(`/bookmarks/${id}`); toast.success(t("conf.bookmarked")); }
      else { await api.post(`/bookmarks`, { conference_id: id }); toast.success(t("conf.bookmarked")); hapticSuccess(); }
      refresh();
    } catch { toast.error("Failed"); }
  }

  async function toggleGoing() {
    if (!user || !user.role) { toast.error(t("common.pleaseSignIn")); return; }
    try {
      const { data } = await api.post(`/conferences/${id}/attend`, { going: !isGoing });
      setAttendingCount(data.attending_count);
      toast.success(data.going ? t("conf.going") : t("conf.imAttending"));
      refresh();
    } catch { toast.error("Failed"); }
  }

  async function submitReview() {
    if (!user || !user.role) { toast.error(t("common.pleaseSignIn")); return; }
    if (!newReview.trim()) { toast.error(t("conf.reviewPlaceholder")); return; }
    try {
      await api.post(`/reviews`, { conference_id: id, rating: newRating, body: newReview.trim() });
      setNewReview(""); setNewRating(5);
      loadReviews();
      toast.success(t("conf.postReview"));
    } catch { toast.error("Failed"); }
  }

  async function shareConference() {
    const link = `${process.env.REACT_APP_BACKEND_URL}/api/share/c/${id}`;
    const res = await nativeShare({
      title: conf?.title || "Symposia",
      text: `${conf?.topic || ""} · ${conf?.city || ""}`,
      url: link,
    });
    if (res.method === "clipboard") {
      toast.success(t("conf.shareCopied"));
    } else if (res.method === "unsupported") {
      toast.error("Couldn't share");
    } else if (res.method === "native" || res.method === "web-share") {
      hapticLight();
    }
  }

  if (loading) return <div className="px-6 md:px-10 lg:px-16 py-16">{t("common.loading")}</div>;
  if (!conf) return <div className="px-6 md:px-10 lg:px-16 py-16">{t("common.notFound")}</div>;

  const icsUrl = `${API}/conferences/${id}/ics`;
  const cfp = conf.cfp_deadline ? { date: new Date(conf.cfp_deadline), days: differenceInDays(new Date(conf.cfp_deadline), new Date()), past: isPast(new Date(conf.cfp_deadline)) } : null;

  // JSON-LD structured data — Event schema so Google renders rich event cards
  // in search results (dates, venue, image thumb). Ref: https://schema.org/Event
  // Validated against Google Rich Results guidelines:
  //   - required: name, startDate, location
  //   - offers omitted unless a real price is known (Google requires
  //     price + priceCurrency together; keeping a bare URL trips a warning)
  //   - performer added when the conference has published speakers
  const siteOrigin = (typeof window !== "undefined" ? window.location.origin : "https://conferencely.org");
  const performers = (conf.speakers || [])
    .filter((s) => s && s.name)
    .slice(0, 25)
    .map((s) => ({
      "@type": "Person",
      "name": s.name,
      ...(s.title || s.organization ? { "jobTitle": [s.title, s.organization].filter(Boolean).join(", ") } : {}),
      ...(s.website ? { "url": s.website } : {}),
    }));
  const hasGeo = Number(conf.lat) !== 0 || Number(conf.lng) !== 0;
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Event",
    "name": conf.title,
    "description": conf.description || `${conf.topic} conference in ${conf.city}, ${conf.country}.`,
    "startDate": conf.start_date,
    "endDate": conf.end_date,
    "eventStatus": "https://schema.org/EventScheduled",
    "eventAttendanceMode": conf.type === "virtual"
      ? "https://schema.org/OnlineEventAttendanceMode"
      : "https://schema.org/OfflineEventAttendanceMode",
    "location": {
      "@type": "Place",
      "name": conf.location_name || `${conf.city}, ${conf.country}`,
      "address": {
        "@type": "PostalAddress",
        "addressLocality": conf.city || "",
        "addressCountry": conf.country || "",
      },
      ...(hasGeo
        ? { "geo": { "@type": "GeoCoordinates", "latitude": conf.lat, "longitude": conf.lng } }
        : {}),
    },
    "image": [`${API}/og/conference/${id}.png`],
    "url": `${siteOrigin}/conferences/${id}`,
    "about": conf.topic,
    "keywords": (conf.tags || []).join(", "),
    "organizer": {
      "@type": "Organization",
      "name": "Symposia",
      "url": siteOrigin,
    },
    ...(performers.length > 0 ? { "performer": performers } : {}),
  };

  return (
    <div data-testid="conference-detail">
      <script
        type="application/ld+json"
        data-testid="jsonld-event"
        dangerouslySetInnerHTML={{
          // Escape `</`, `<!--`, and JS line/paragraph terminators so a
          // hostile conference title like `</script><script>alert(1)` can't
          // break out of the JSON-LD block into executable HTML/JS.
          __html: JSON.stringify(jsonLd)
            .replace(/</g, "\\u003c")
            .replace(/-->/g, "--\\u003e")
            .replace(/\u2028/g, "\\u2028")
            .replace(/\u2029/g, "\\u2029"),
        }}
      />
      <div className="px-6 md:px-10 lg:px-16 pt-8">
        <Link to="/conferences" data-testid="back-to-conferences" className="inline-flex items-center gap-1.5 text-sm text-zinc-500 hover:text-zinc-950">
          <ArrowLeft size={14} data-arrow="forward" /> {t("conf.backToConfs")}
        </Link>
      </div>

      <section className="px-6 md:px-10 lg:px-16 pt-8 pb-0">
        <div className="relative">
          <div className="aspect-[16/8] w-full bg-zinc-100 border border-zinc-200 overflow-hidden">
            <img src={confImage(conf, { w: 1920, h: 960 })} alt={conf.title} className="img-mono w-full h-full object-cover" />
          </div>
          <div className="md:-mt-20 md:ml-0 bg-white md:max-w-4xl md:p-10 p-6 border border-zinc-200 relative z-10">
            <div className="flex flex-wrap items-center gap-2 mb-5">
              <span className="tag-brand">{conf.topic}</span>
              <span className="tag">{conf.type === "tech" ? t("common.industry") : t("common.academic")}</span>
              {isCurrentlySponsored(conf) && <span className="tag-solid" data-testid="conf-sponsored-badge">✦ {t("conf.sponsored")}</span>}
              {conf.featured && !isCurrentlySponsored(conf) && <span className="tag-solid">{t("conf.featured")}</span>}
              {conf.tags?.slice(0, 3).map((tg) => (<span key={tg} className="tag">{tg}</span>))}
            </div>
            <h1 className="font-display font-black text-5xl md:text-6xl lg:text-7xl tracking-[-0.04em] leading-[.92]" data-testid="conf-title">{conf.title}</h1>
            <div className="mt-6 text-zinc-600 text-lg leading-relaxed max-w-3xl">{conf.description}</div>
          </div>
        </div>
      </section>

      <section className="px-6 md:px-10 lg:px-16 pt-14">
        <div className="grid md:grid-cols-12 gap-10">
          <div className="md:col-span-8">
            <div className="grid sm:grid-cols-3 gap-px bg-zinc-200 border border-zinc-200">
              <Meta icon={<Calendar size={14} />} label={t("conf.dates")} value={`${format(new Date(conf.start_date), "MMM d")} – ${format(new Date(conf.end_date), "MMM d, yyyy")}`} />
              <Meta icon={<MapPin size={14} />} label={t("conf.location")} value={conf.location_name} sub={`${conf.city}, ${conf.country}`} />
              {conf.website ? (
                <Meta icon={<Globe size={14} />} label={t("conf.website")} value={<a href={conf.website} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 hover:text-blue-700">{tryHost(conf.website)} <ArrowUpRight size={12} data-arrow="forward" /></a>} />
              ) : (
                <Meta icon={<Globe size={14} />} label={t("conf.website")} value="—" />
              )}
            </div>

            {cfp && (
              <div className="mt-8 border-2 border-blue-600 bg-blue-50 p-5 flex items-start gap-4" data-testid="conf-cfp">
                <CalendarPlus size={24} className="text-blue-700 mt-0.5" weight="duotone" />
                <div className="flex-1">
                  <div className="overline" style={{color:'#1D4ED8'}}>{t("conf.callForPapers")}</div>
                  <div className="font-display font-bold text-2xl mt-1 tracking-tight">
                    {cfp.past ? t("conf.submissionsClosed") : t("conf.daysLeft", { days: cfp.days })}
                  </div>
                  <div className="text-sm text-zinc-600 mt-1 font-mono">{t("conf.deadline", { date: format(cfp.date, "MMMM d, yyyy") })}</div>
                  {conf.cfp_url && (
                    <a href={conf.cfp_url} target="_blank" rel="noreferrer" data-testid="conf-cfp-link" className="inline-flex items-center gap-1 text-sm text-blue-700 underline mt-2">{t("conf.submissionPage")} <ArrowUpRight size={12} data-arrow="forward" /></a>
                  )}
                </div>
              </div>
            )}

            {conf.papers?.length > 0 && (
              <div className="mt-14">
                <div className="flex items-end justify-between border-b border-zinc-950 pb-3 mb-6">
                  <h2 className="font-display font-bold text-2xl tracking-tight">{t("conf.papers")}</h2>
                  <span className="font-mono text-xs text-zinc-500">{conf.papers.length}</span>
                </div>
                <div className="border border-zinc-200 divide-y divide-zinc-200">
                  {conf.papers.map((p, i) => (
                    <Link key={p.id} to={`/papers/${p.id}`} data-testid={`conf-paper-${i}`} className="block px-5 py-4 hover:bg-[#FAFAFA] group">
                      <div className="flex items-start gap-4">
                        <FileText size={16} className="mt-1 text-zinc-400" />
                        <div className="flex-1">
                          <div className="font-display font-semibold text-base group-hover:text-blue-700 transition-colors">{p.title}</div>
                          <div className="text-xs text-zinc-500 mt-1 font-mono">{(p.authors || []).join(", ")} {p.track && `· ${p.track}`}</div>
                        </div>
                        <ArrowUpRight size={14} data-arrow="forward" className="text-zinc-300 group-hover:text-blue-700 transition-colors mt-1" />
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {/* Reviews */}
            <div className="mt-14" data-testid="conf-reviews">
              <div className="flex items-end justify-between border-b border-zinc-950 pb-3 mb-6">
                <div>
                  <h2 className="font-display font-bold text-2xl tracking-tight">{t("conf.reviews")}</h2>
                  {reviews.count > 0 && (
                    <div className="mt-1 flex items-center gap-2 text-sm text-zinc-600">
                      <div className="flex">
                        {[1,2,3,4,5].map(n => <Star key={n} size={14} weight={n <= Math.round(reviews.avg) ? "fill" : "regular"} className="text-blue-600" />)}
                      </div>
                      <span className="font-mono text-xs">{t("conf.reviewsSummary", { avg: reviews.avg.toFixed(1), n: reviews.count })}</span>
                    </div>
                  )}
                </div>
              </div>

              {user && user.role && (
                <div className="border border-zinc-200 p-5 mb-6 bg-[#FAFAFA]">
                  <div className="overline mb-3">{t("conf.yourReview")}</div>
                  <div className="flex items-center gap-1 mb-3">
                    {[1,2,3,4,5].map(n => (
                      <button key={n} onClick={() => setNewRating(n)} data-testid={`review-star-${n}`} className="p-0.5">
                        <Star size={22} weight={n <= newRating ? "fill" : "regular"} className="text-blue-600 hover:scale-110 transition-transform" />
                      </button>
                    ))}
                    <span className="ml-2 font-mono text-xs text-zinc-500">{newRating}/5</span>
                  </div>
                  <textarea data-testid="review-body" value={newReview} onChange={(e) => setNewReview(e.target.value)} placeholder={t("conf.reviewPlaceholder")} className="input-box w-full min-h-[80px]" />
                  <div className="mt-3 flex justify-end">
                    <button data-testid="review-submit" onClick={submitReview} className="btn-primary">{t("conf.postReview")}</button>
                  </div>
                </div>
              )}

              <div className="border border-zinc-200 divide-y divide-zinc-200">
                {reviews.items.length === 0 && <div className="p-8 text-center text-zinc-500 text-sm">{t("conf.noReviews")}</div>}
                {reviews.items.map((r, i) => (
                  <div key={r.id} data-testid={`review-${i}`} className="px-5 py-4">
                    <div className="flex items-center justify-between">
                      <div className="font-display font-semibold text-sm">{r.user_name}</div>
                      <div className="flex">
                        {[1,2,3,4,5].map(n => <Star key={n} size={12} weight={n <= r.rating ? "fill" : "regular"} className="text-blue-600" />)}
                      </div>
                    </div>
                    <p className="text-sm text-zinc-700 mt-1.5 whitespace-pre-line">{r.body}</p>
                    <div className="font-mono text-[10px] text-zinc-400 mt-2">{r.created_at?.slice(0, 10)}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <aside className="md:col-span-4">
            <div className="md:sticky md:top-24 space-y-3">
              <button onClick={toggleGoing} data-testid="conf-going-btn" className={(isGoing ? "btn-accent" : "btn-primary") + " w-full justify-center"}>
                {isGoing ? <CheckCircle size={16} weight="fill" /> : <CheckCircle size={16} />}
                {isGoing ? t("conf.going") : t("conf.imAttending")}
              </button>
              {attendingCount > 0 && (
                <div className="text-xs text-zinc-500 text-center font-mono" data-testid="attending-count">{t("conf.attendees", { n: attendingCount })}</div>
              )}

              <button onClick={toggleBookmark} data-testid="conf-bookmark-btn" className="btn-secondary w-full justify-center">
                <BookmarkSimple size={16} weight={isBookmarked ? "fill" : "regular"} />
                {isBookmarked ? t("conf.bookmarked") : t("conf.save")}
              </button>

              <a href={icsUrl} download data-testid="conf-ics-export" className="btn-secondary w-full justify-center">
                <Calendar size={16} /> {t("conf.addToCalendar")}
              </a>

              <button onClick={shareConference} data-testid="conf-share-btn" className="btn-secondary w-full justify-center">
                <ShareNetwork size={16} /> {t("conf.share")}
              </button>

              {!isCurrentlySponsored(conf) && (
                <Link to={`/sponsor?conference_id=${conf.id}`} data-testid="conf-sponsor-btn" className="btn-ghost w-full justify-center text-xs">
                  ✦ {t("conf.sponsorListing")}
                </Link>
              )}

              {conf.speakers?.length > 0 && (
                <div className="pt-3">
                  <div className="overline mb-3">{t("conf.speakers")}</div>
                  <div className="border border-zinc-200 divide-y divide-zinc-200">
                    {conf.speakers.map((s, i) => (
                      <Link key={s.id} to={`/speakers/${s.id}`} data-testid={`conf-speaker-${i}`} className="flex items-center gap-3 px-4 py-3 hover:bg-[#FAFAFA] group">
                        <div className="w-11 h-11 bg-zinc-100 border border-zinc-200 flex-shrink-0 overflow-hidden">
                          {s.image_url ? <img src={s.image_url} alt="" className="img-mono w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center font-display font-black text-zinc-400">{s.name[0]}</div>}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-display font-semibold text-sm leading-tight truncate group-hover:text-blue-700 transition-colors">{s.name}</div>
                          <div className="text-xs text-zinc-500 truncate font-mono">{s.organization}</div>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}

              <div className="border border-zinc-200 p-5 bg-[#FAFAFA] mt-3">
                <div className="overline mb-2">{t("conf.citation")}</div>
                <div className="font-mono text-xs text-zinc-600 leading-relaxed">
                  {conf.issn && conf.issn.length > 0 && <div data-testid="conf-issn">ISSN: {conf.issn.join(", ")}</div>}
                  {conf.host_organization && <div className="mt-1">{t("conf.host")}: {conf.host_organization}</div>}
                  <div className="mt-1">Symposia Index № {conf.id.slice(0, 6)}</div>
                  <div>{t("conf.retrieved", { date: format(new Date(), "yyyy-MM-dd") })}</div>
                </div>
              </div>

              {/* Travel notes — visa, climate, flights, currency (curated per country) */}
              <TravelSidebar conf={conf} />

              {(conf.type === "academic" || conf.type === "both") && hasProceedings(conf.proceedings) && (
                <div className="border-2 border-zinc-950 p-5 bg-white mt-3" data-testid="conf-proceedings">
                  <div className="flex items-center gap-2 mb-3">
                    <Books size={16} weight="duotone" className="text-blue-700" />
                    <div className="overline">{t("conf.proceedings")}</div>
                  </div>
                  <ProceedingsRows p={conf.proceedings} t={t} />
                </div>
              )}
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
}

function Meta({ icon, label, value, sub }) {
  return (
    <div className="bg-white p-5">
      <div className="overline flex items-center gap-1.5 mb-2">{icon}{label}</div>
      <div className="font-medium text-base leading-tight">{value}</div>
      {sub && <div className="text-xs text-zinc-500 mt-1 font-mono">{sub}</div>}
    </div>
  );
}

function tryHost(url) { try { return new URL(url).hostname; } catch { return url; } }

function isCurrentlySponsored(conf) {
  if (!conf || !conf.sponsored_until) return false;
  try { return new Date(conf.sponsored_until).getTime() > Date.now(); }
  catch { return false; }
}

function hasProceedings(p) {
  if (!p) return false;
  return Boolean(p.publisher || p.doi || p.isbn || p.url || (p.indexed_papers_count && p.indexed_papers_count > 0));
}

function ProceedingsRows({ p, t }) {
  const rows = [];
  if (p.publisher) rows.push([t("conf.publisher"), p.publisher]);
  if (p.doi) rows.push(["DOI", <a key="doi" href={p.doi.startsWith("http") ? p.doi : `https://doi.org/${p.doi}`} target="_blank" rel="noreferrer" className="underline hover:text-blue-700" data-testid="proc-doi">{p.doi}</a>]);
  if (p.isbn) rows.push(["ISBN", p.isbn]);
  if (p.volume) rows.push([t("conf.volume"), p.volume]);
  if (p.indexed_papers_count) rows.push([t("conf.papers"), t("conf.indexedPapers", { n: p.indexed_papers_count })]);
  return (
    <>
      <div className="space-y-1.5 font-mono text-xs text-zinc-700">
        {rows.map(([k, v]) => (
          <div key={k} className="flex gap-2"><span className="text-zinc-400 min-w-[5.5rem]">{k}</span><span className="flex-1 break-all">{v}</span></div>
        ))}
      </div>
      {p.url && (
        <a href={p.url} target="_blank" rel="noreferrer" data-testid="proc-url" className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-blue-700 hover:text-blue-900 link-ul">
          {t("conf.proceedingsUrl")} <ArrowUpRight size={12} data-arrow="forward" />
        </a>
      )}
    </>
  );
}
